import { Message } from "google-protobuf";
import i18next from "i18next";
import { isArray, isPlainObject, upperFirst } from "lodash";
import * as errors from "../constants/errors";
import { EVENT_NEW_SNACKBAR } from "../constants/events";
import { CHANNEL_ADD_ADMIN, CHANNEL_ADD_MEMBER, CHANNEL_GET_MESSAGES_STATS, GROUP_ADD_ADMIN, GROUP_ADD_MEMBER, GROUP_CHANGE_MEMBER_RIGHTS } from "../constants/methods";
import { protoRequestsObjectType, protoRequestsTable, protoResponsesObjectType } from "../constants/protoTable";
import { ChannelAddMember } from "../proto/ChannelAddMember_pb";
import { ErrorResponse } from "../proto/Error_pb";
import { Pagination, RoomDraft, RoomMessageContact, RoomMessageForwardFrom, RoomMessageLocation, RoomMessageStoryReply } from "../proto/Global_pb";
import { GroupAddAdmin } from "../proto/GroupAddAdmin_pb";
import { GroupAddMember } from "../proto/GroupAddMember_pb";
import { GroupChangeMemberRights } from "../proto/GroupChangeMemberRights_pb";
import { UserContactsImport } from "../proto/UserContactsImport_pb";
import Api, { ApiOptions } from "./Api";
import Client from "./Api/Client";
import { logOut } from "./auth";
import { secondToTime } from "./date";
import ServerError from "./Error/ServerError";
import { generateRandomId, isDevMod, msSleep, randomString } from "./utils";
import { ChannelGetMessagesStats } from "../proto/ChannelGetMessagesStats_pb";
import MockSocketClient, { mockRequestMap } from "./Api/client-mock";
import { ChannelAddAdmin } from "../proto/ChannelAddAdmin_pb";
import { ERROR_TIMEOUT } from "../constants/errors";

export const setProtoParams = (actionId: keyof protoRequestsObjectType, proto: any, params: any): void => {
  Object.keys(params).forEach((paramKey) => {
    const protoMap: { [paramKey: string]: any } = {
      contact: RoomMessageContact,
      contactsList: UserContactsImport.Contact,
      draft: RoomDraft,
      forwardFrom: RoomMessageForwardFrom,
      pagination: Pagination,
      location: RoomMessageLocation,
      // @ts-ignore
      member: { [GROUP_ADD_MEMBER]: GroupAddMember.Member, [CHANNEL_ADD_MEMBER]: ChannelAddMember.Member }[actionId],
      // @ts-ignore
      permission: {
        [GROUP_CHANGE_MEMBER_RIGHTS]: GroupChangeMemberRights.MemberRights,
        [GROUP_ADD_ADMIN]: GroupAddAdmin.AdminRights,
        [CHANNEL_ADD_ADMIN]: ChannelAddAdmin.AdminRights,
      }[actionId],
      storyReply: RoomMessageStoryReply,
      messageIdInfo: ChannelGetMessagesStats.messageIdInfo,
      messageIdInfoList: ChannelGetMessagesStats.messageIdInfo,
    };
    let value = params[paramKey];
    if (value !== undefined && protoMap[paramKey] !== undefined) {
      if (isArray(value)) {
        value = value.map((param: any) => {
          const objectClass = new protoMap[paramKey]();
          setProtoParams(actionId, objectClass, param);
          return objectClass;
        });
      } else if (isPlainObject(value)) {
        const objectClass = new protoMap[paramKey]();

        setProtoParams(actionId, objectClass, value);
        value = objectClass;
      }
    }
    proto[`set${upperFirst(paramKey)}`](value);
  });
};

const showError = (error: ErrorResponse.AsObject): void => {
  let message;
  if (error.wait > 0) {
    message = `${i18next.t("global.please_wait")}: ${secondToTime(error.wait)}`;
  }
  if (i18next.exists(`errors.${error.majorCode}_${error.minorCode}`)) {
    message = i18next.t(`errors.${error.majorCode}_${error.minorCode}`);
  } else if (i18next.exists(`errors.${error.majorCode}`)) {
    message = i18next.t(`errors.${error.majorCode}`);
  }
  if (message === undefined && isDevMod) {
    return console.error("handleOnError:", error);
  }
  if (message !== undefined) {
    document.dispatchEvent(
      new CustomEvent(EVENT_NEW_SNACKBAR, {
        detail: {
          message: message,
          options: { variant: "error" },
        },
      })
    );
  }
};

const handleOnError = (error: ErrorResponse.AsObject): void => {
  const ignoredList = [
    errors.ERROR_BAD_REQUEST,
    errors.ERROR_CLIENT_GET_ROOM_MESSAGE_NOT_FOUND,
    errors.ERROR_USER_VERIFY_TWO_STEP_VERIFICATION_ENABLED,
    errors.ERROR_CLIENT_GET_ROOM_HISTORY_NOT_FOUND,
    errors.ERROR_CLIENT_SEARCH_ROOM_HISTORY_NOT_FOUND,
    errors.ERROR_USER_TWO_STEP_VERIFICATION_GET_PASSWORD_DETAIL_NO_PASSWORD,
    errors.ERROR_CHAT_SET_ACTION_FORBIDDEN,
    errors.ERROR_GROUP_SET_ACTION_FORBIDDEN,
    errors.ERROR_TIMEOUT,
    errors.ERROR_FORBIDDEN,
    errors.ERROR_CHAT_SET_ACTION_BAD_PAYLOAD,
    errors.ERROR_GROUP_SET_ACTION_BAD_PAYLOAD,
  ];
  if (ignoredList.includes(error.majorCode)) return;

  const logoutList = [
    errors.ERROR_USER_LOGIN_BAD_PAYLOAD,
    errors.ERROR_USER_LOGIN_FAILED,
    errors.ERROR_USER_LOGIN_ALREADY_LOGGED_IN,
    errors.ERROR_USER_LOGIN_INTERNAL_SERVER_ERROR,
    errors.ERROR_USER_TWO_STEP_VERIFICATION_GET_PASSWORD_DETAIL_INTERNAL_SERVER_ERROR,
  ];
  if (logoutList.includes(error.majorCode)) {
    logOut().then();
    return;
  }

  const reconnectList = [errors.ERROR_LOGIN_REQUIRED];
  if (reconnectList.includes(error.majorCode)) {
    console.error("SOCKET CLOSED FROM proto.ts handleOnError");
    return Client.instance.close();
  }

  // showError(error);
};

export const protoHandler = <T extends keyof protoRequestsObjectType>(
  actionId: T,
  params: Omit<Partial<protoRequestsObjectType[T]>, "request">,
  options?: Partial<ApiOptions>
): Promise<protoResponsesObjectType[T]> => {
  if (window.Cypress) {
    console.log("PROTOHANDLER", actionId);
    const request = { request: { id: randomString(10) }, actionId, params, options };
    return MockSocketClient.instance.sendMessage(request);
  }
  const proto = new protoRequestsTable[actionId]();
  setProtoParams(actionId, proto, params);
  return new Promise<any>((resolve, reject) => {
    Api.invoke(actionId, proto, options)
      .then((response: Message) => resolve(response.toObject()))
      .catch((error: ServerError) => {
        const e: ErrorResponse.AsObject = error.errorResponse.toObject();
        let retry = options?.retry || 3;
        const shouldResend = options?.retry !== 0 && e.majorCode === ERROR_TIMEOUT && e.minorCode === 1;
        if (!shouldResend) {
          handleOnError(e);

          return reject(e);
        }
        return (
          msSleep(1000)
            .then(protoHandler.bind(null, actionId, params, { ...options, retry: retry - 1 }))
            //  @ts-ignore
            .then((res: any) => resolve(res))
            .catch(reject)
        );
      });
  });
};
